"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ArrowRight, Heart } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function UnitedInFaith() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="129181" id="understanding-pda-in-australia" className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-background to-secondary/30 py-20 lg:py-0">
      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-primary/5 blur-[100px] animate-pulse" style={{
        animationDuration: '8s'
      }} />
        <div className="absolute top-[30%] -right-[10%] w-[50%] h-[70%] rounded-full bg-secondary/10 blur-[120px] animate-pulse" style={{
        animationDuration: '12s'
      }} />
        <div className="absolute -bottom-[20%] left-[20%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[100px] animate-pulse" style={{
        animationDuration: '10s'
      }} />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col gap-6 items-start transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="secondary" className="bg-secondary/20 text-primary hover:bg-secondary/30 border-none rounded-full px-4 py-1.5 backdrop-blur-sm">
              <Heart className="w-4 h-4 mr-2 text-accent" />
              Supporting PDA Families
            </Badge>

            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.2] text-primary tracking-tight" style={{
            textShadow: '0 4px 32px var(--color-secondary)'
          }}>
              Empowering PDA Families Across Australia
            </h1>

            <p className="font-sans text-lg md:text-xl text-foreground/80 max-w-lg leading-relaxed">
              Providing professional, calm, and authoritative guidance for parents and teachers. Discover high-quality articles and downloadable guides tailored for the Australian PDA community.
            </p>

            <div className="flex flex-wrap gap-4 mt-4">
              <Button size="lg" className="rounded-full px-8 h-14 text-base bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/20" asChild>
                <Link to="/articles/">
                  Explore Resources <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-8 h-14 text-base bg-background/50 backdrop-blur-md border-primary/20 text-primary hover:bg-primary/5" asChild>
                <Link to="/start-here/">
                  About PDA
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative w-full max-w-md mx-auto lg:max-w-none lg:ml-auto transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            {}
            <div className="relative rounded-[3rem] md:rounded-[5rem] rounded-tl-[1rem] md:rounded-tl-[2rem] rounded-br-[1rem] md:rounded-br-[2rem] overflow-hidden shadow-2xl border border-border/40 bg-muted">
              <AspectRatio ratio={4 / 5}>
                <img src="https://pdaaustralia.com.au/wp-content/uploads/2026/03/Mother-and-son-in-serene-nature.png" alt="Australian parent and child in a warm, natural outdoor setting" className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
              </AspectRatio>
            </div>

            {}
            <div className="absolute -bottom-6 -left-4 md:-bottom-10 md:-left-12 bg-background/90 backdrop-blur-xl border border-border/50 p-5 md:p-6 rounded-3xl shadow-2xl flex items-center gap-4 z-20 animate-in slide-in-from-bottom-8 duration-1000 delay-700 fill-mode-both">
              <div className="bg-secondary/30 p-3 md:p-4 rounded-2xl text-primary shrink-0">
                <SafeHydrate><Calendar className="w-6 h-6 md:w-8 md:h-8" /></SafeHydrate>
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
                  Latest Resource
                </p>
                <Link to="https://pdaaustralia.com.au/product/funded-ndis-guide-pda/" newTab className="text-base md:text-lg font-bold text-primary">
                  NDIS Funding Guide
                </Link>
              </div>
            </div>
            
            {}
            <div className="absolute -inset-4 bg-gradient-to-tr from-secondary/30 to-primary/10 rounded-[4rem] rounded-tl-[2rem] rounded-br-[2rem] -z-10 blur-2xl opacity-40" />
          </div>

        </div>
      </div>
    </section>;
}